import React, { useState, useRef, useEffect, useMemo } from 'react';
import { gorunurDersler, dersLabel } from '../../lib/dersler';
import { cn } from './cn';

// Çoklu ders seçimi (2026-07-17 panel ergonomi) — checkbox'lı açılır menü.
// Kontrollü + fetch'siz: seçenekler gorunurDersler(izinliDersler)'den gelir
// (kısıtlı kullanıcı yalnız izinli derslerini görür), TYT/AYT gruplu listelenir.
//   secili: string[]           ([] = Tüm Dersler)
//   onChange: (yeni) => void   (her değişimde YENİ dizi emit edilir)
//   izinliDersler: string[]|null
export default function DersMultiSelect({ secili, onChange, izinliDersler }) {
  const [acik, setAcik] = useState(false);
  const kokRef = useRef(null);

  // Dış-tık + Escape kapatır.
  useEffect(() => {
    if (!acik) return;
    const disTik = (e) => { if (kokRef.current && !kokRef.current.contains(e.target)) setAcik(false); };
    const tus = (e) => { if (e.key === 'Escape') setAcik(false); };
    document.addEventListener('mousedown', disTik);
    document.addEventListener('keydown', tus);
    return () => {
      document.removeEventListener('mousedown', disTik);
      document.removeEventListener('keydown', tus);
    };
  }, [acik]);

  const kisitli = Array.isArray(izinliDersler) && izinliDersler.length > 0;

  // '' (Tüm Dersler) seçeneği hariç, TYT/AYT önekine göre iki grup.
  const gruplar = useMemo(() => {
    const secenekler = gorunurDersler(izinliDersler).filter(d => d.value !== '');
    return [
      { ad: 'TYT', dersler: secenekler.filter(d => d.value.startsWith('tyt_')) },
      { ad: 'AYT', dersler: secenekler.filter(d => d.value.startsWith('ayt_')) },
    ].filter(g => g.dersler.length > 0);
  }, [izinliDersler]);

  const seciliSet = useMemo(() => new Set(secili), [secili]);

  const toggle = (value) => {
    onChange(seciliSet.has(value) ? secili.filter(v => v !== value) : [...secili, value]);
  };

  // Grup "tümünü seç" / "kaldır": grubun hepsi seçiliyse gruptakileri çıkar,
  // değilse eksikleri ekle.
  const grupToggle = (grup) => {
    const degerler = grup.dersler.map(d => d.value);
    const hepsiSecili = degerler.every(v => seciliSet.has(v));
    if (hepsiSecili) onChange(secili.filter(v => !degerler.includes(v)));
    else onChange([...new Set([...secili, ...degerler])]);
  };

  const butonEtiket = secili.length === 0
    ? (kisitli ? 'Tüm Derslerim' : 'Tüm Dersler')
    : secili.length === 1
    ? dersLabel(secili[0])
    : `Dersler (${secili.length})`;

  return (
    <div ref={kokRef} className="relative">
      <button
        type="button"
        onClick={() => setAcik(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={acik}
        title="Ders (çoklu seçim)"
        className={cn(
          'px-2 py-1.5 rounded border text-xs bg-white flex items-center gap-1 max-w-[180px]',
          secili.length > 0 ? 'border-blue-400 text-blue-700 font-semibold' : 'border-gray-300 text-gray-700'
        )}
      >
        <span className="truncate">{butonEtiket}</span>
        <span className="text-gray-400 shrink-0">▾</span>
      </button>

      {acik && (
        <div className="absolute z-40 mt-1 left-0 w-64 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2" role="listbox">
          <button
            type="button"
            onClick={() => onChange([])}
            className={cn(
              'w-full text-left px-2 py-1.5 rounded text-xs font-semibold hover:bg-gray-50',
              secili.length === 0 ? 'text-blue-700 bg-blue-50' : 'text-gray-700'
            )}
          >
            {kisitli ? 'Tüm Derslerim' : 'Tüm Dersler'}{secili.length > 0 && <span className="text-gray-400 font-normal"> (temizle)</span>}
          </button>

          {gruplar.map(grup => {
            const hepsiSecili = grup.dersler.every(d => seciliSet.has(d.value));
            return (
              <div key={grup.ad} className="mt-1.5">
                <div className="flex items-center justify-between px-2 py-1">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{grup.ad}</span>
                  <button
                    type="button"
                    onClick={() => grupToggle(grup)}
                    className="text-[10px] text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {hepsiSecili ? 'kaldır' : 'tümünü seç'}
                  </button>
                </div>
                {grup.dersler.map(d => (
                  <label
                    key={d.value}
                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer text-xs text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={seciliSet.has(d.value)}
                      onChange={() => toggle(d.value)}
                      className="accent-blue-600"
                    />
                    {/* Grup başlığı zaten TYT/AYT dediği için etiketin öneki kırpılır */}
                    <span>{d.label.replace(/^(TYT|AYT) /, '')}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
